/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: ~147 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MYBROADCASTS_RESEND_CLEANUP_DEBUG
 * Date:         2026-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Remove debug logging added during troubleshooting. Hook now accepts single item
 * instead of broadcastId+allBroadcasts, creating new broadcast with only that item.
 *
 * File Size Check:
 * - Current: ~142 lines (with debug)
 * - Removing: ~25 lines (debug logs)
 * - Projected: ~117 lines
 * - Decision: Proceed (back within 100-line hook limit)
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx
 * - client/src/services/broadcastService.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    RESEND_SINGLE_ITEM_HOOK_FIX
 * Date:         2026-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Update useBroadcastActions hook to accept a single broadcast item and create 
 * a new broadcast with only that item. Removed broadcast group finding logic 
 * and updated payload creation.
 *
 * File Size Check:
 * - Current: 130 lines
 * - Adding: ~20 lines (header)
 * - Projected: 150 lines
 * - Decision: Proceed (Hook, code lines ~87, under 100 limit)
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MYBROADCASTS_RESEND_DEBUG_LOGGING
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Add comprehensive debug logging to identify root cause of 400 error
 * when resending broadcasts. Logs full payload and server error response.
 *
 * File Size Check:
 * - Current: 95 lines
 * - Adding: ~8 lines (debug logs)
 * - Projected: 103 lines
 * - Decision: Proceed (acceptable temp debugging)
 *
 * Related Screens / Modules:
 * - client/src/services/broadcastService.ts
 * - server/utils/schemas/broadcast.yup.js
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MYBROADCASTS_PHASE2_RESEND_HOOK_FIX
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Fix useBroadcastActions hook:
 * 1. Replace react-toastify with centralized notify helper.
 * 2. Fix type mismatch when mapping BroadcastItem to BroadcastItemCreate.
 *
 * File Size Check:
 * - Current file: ~85 lines
 * - Limit: 100 lines (hooks)
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx
 * - client/src/services/broadcastService.ts
 * - client/src/utils/notifications.ts
 */

import { useState } from 'react';
import { notify } from '../../../../../utils/notifications';
import { resendBroadcast } from '../../../../../services/broadcastService';

interface UseBroadcastActionsReturn {
  handleResend: (item: any) => Promise<void>;
  isResending: boolean;
}

export const useBroadcastActions = (
  refreshCallback: () => void
): UseBroadcastActionsReturn => {
  const [isResending, setIsResending] = useState<boolean>(false);

  // [2026-02-06] Resend handler - uses proper resend API endpoint (updates existing broadcast)
  const handleResend = async (item: any): Promise<void> => {
    if (!item.broadcastId) {
      notify.error(null, "Cannot resend: broadcast ID is missing");
      return;
    }

    setIsResending(true);
    try {
      // [2026-02-06] Call resend API - updates existing broadcast, increments resendCount
      await resendBroadcast(item.broadcastId);

      notify.success('Broadcast resent successfully');
      refreshCallback();

    } catch (error: any) {
      console.error('Resend broadcast error:', error);
      // [2026-02-06] BR-22: Handle 24-hour resend limit error
      const errorCode = error.response?.data?.error;
      if (errorCode === 'RESEND_TOO_SOON') {
        const hoursRemaining = error.response?.data?.hoursRemaining || 24;
        notify.error(null, `You can resend this broadcast in ${hoursRemaining} hour${hoursRemaining === 1 ? '' : 's'}`);
      } else {
        notify.error(error, "We couldn't resend the broadcast. Please try again");
      }
    } finally {
      setIsResending(false);
    }
  };

  return {
    handleResend,
    isResending,
  };
};
